import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCollections, getCollectionDecks, createDeck, deleteDeck, Deck, Collection } from '../api';

function CreateDeckModal({ collectionId, onClose, onCreated }: { collectionId: number; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [langPair, setLangPair] = useState<'en-vi' | 'ja-vi'>('en-vi');
  const [desc, setDesc] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    await createDeck({ name: name.trim(), language_pair: langPair, description: desc.trim(), collection_id: collectionId });
    setLoading(false);
    onCreated();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Thêm bộ từ mới</h3>
        <div className="form-group">
          <label>Tên bộ từ *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="VD: JLPT N4 – Chủ đề du lịch" autoFocus />
        </div>
        <div className="form-group">
          <label>Ngôn ngữ</label>
          <select value={langPair} onChange={e => setLangPair(e.target.value as 'en-vi' | 'ja-vi')}>
            <option value="en-vi">🇬🇧 Anh → Việt</option>
            <option value="ja-vi">🇯🇵 Nhật → Việt</option>
          </select>
        </div>
        <div className="form-group">
          <label>Mô tả (tuỳ chọn)</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Mô tả ngắn về bộ từ..." />
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Huỷ</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !name.trim()}>
            {loading ? 'Đang tạo...' : 'Tạo bộ từ'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CollectionPage() {
  const { id } = useParams<{ id: string }>();
  const colId = parseInt(id!);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const [cols, d] = await Promise.all([getCollections(), getCollectionDecks(colId)]);
      setCollection(cols.find(c => c.id === colId) || null);
      setDecks(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [colId]);

  const handleDeleteDeck = async (e: React.MouseEvent, deckId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Xoá bộ từ này? Tất cả thẻ sẽ bị xoá.')) return;
    await deleteDeck(deckId);
    load();
  };

  if (loading) return <div className="loading"><div className="spinner" />Đang tải...</div>;
  if (!collection) return <div className="loading">Không tìm thấy bộ sưu tập.</div>;

  return (
    <div>
      <Link to="/" className="back-link">← Trang chủ</Link>

      <div className="collection-page-header" style={{ borderLeftColor: collection.color }}>
        <h1>{collection.name}</h1>
        {collection.description && <p>{collection.description}</p>}
        <div className="collection-page-meta">
          <span>{collection.deck_count || 0} bộ từ</span>
          <span>·</span>
          <span>{collection.card_count || 0} thẻ</span>
          {(collection.due_count || 0) > 0 && (
            <>
              <span>·</span>
              <span className="collection-due">{collection.due_count} cần ôn</span>
            </>
          )}
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '1.75rem' }}>
        <h2>Bộ từ trong bộ sưu tập</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Thêm bộ từ</button>
      </div>

      {decks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <p>Chưa có bộ từ nào. Hãy thêm bộ từ đầu tiên!</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Thêm bộ từ</button>
        </div>
      ) : (
        <div className="deck-grid">
          {decks.map(deck => (
            <Link to={`/deck/${deck.id}`} key={deck.id} className={`deck-card ${deck.language_pair}`}>
              <div className="deck-lang-badge">
                {deck.language_pair === 'en-vi' ? '🇬🇧 Anh – Việt' : '🇯🇵 Nhật – Việt'}
              </div>
              <div className="deck-name">{deck.name}</div>
              {deck.description && <div className="deck-desc">{deck.description}</div>}
              <div className="deck-meta">
                <span className="deck-count">{deck.card_count || 0} thẻ</span>
                <span className={`due-badge ${(deck.due_count || 0) === 0 ? 'zero' : ''}`}>
                  {deck.due_count || 0} cần ôn
                </span>
              </div>
              <div className="deck-actions" onClick={e => e.preventDefault()}>
                <Link to={`/deck/${deck.id}/flashcard`} className="btn btn-jade btn-sm" onClick={e => e.stopPropagation()}>🃏 Flashcard</Link>
                <Link to={`/deck/${deck.id}/quiz`} className="btn btn-accent btn-sm" onClick={e => e.stopPropagation()}>❓ Quiz</Link>
                <Link to={`/deck/${deck.id}/typing`} className="btn btn-amber btn-sm" onClick={e => e.stopPropagation()}>⌨️ Typing</Link>
                <Link to={`/deck/${deck.id}/review`} className="btn btn-gold btn-sm" onClick={e => e.stopPropagation()}>🔁 Ôn tập</Link>
                <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }}
                  onClick={e => handleDeleteDeck(e, deck.id)}>🗑</button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateDeckModal collectionId={colId} onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}
